import React from 'react';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { Trans } from 'react-i18next';
import { AuthorInfo } from '@presentation/components/AuthorInfo';
import { useMediaQuery } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import type { CreatorModel } from '@domain/models';

import {
	BackgroundImage,
	BannerImage,
	LeftColumn,
	Overlay,
	RightColumn,
	Container,
	Wrapper,
	BannerImageContainer,
	BannerTitle,
} from './styles';

export type ArtworkBannerProps = {
	isLoading: boolean;
	id: string;
	title: string;
	description: string;
	imageSrc: string;
	backgroundImageSrc: string;
	creator: CreatorModel | null;
};
export const ArtworkBanner = ({
	isLoading,
	id,
	title,
	description,
	imageSrc,
	backgroundImageSrc,
	creator,
}: ArtworkBannerProps) => {
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

	if (isLoading) {
		return (
			<Wrapper maxWidth='xl'>
				<Container maxWidth='xl' container columnSpacing={{ sm: 0, md: 4 }}>
					<Skeleton variant='rounded' height={200} animation='wave' />
				</Container>
			</Wrapper>
		);
	}

	return (
		<Wrapper maxWidth='xl'>
			<Container
				container
				columnSpacing={{ sm: 0, md: 4 }}
				bgimage={imageSrc}
				component='a'
				href={`/puzzle/${id}`}
				sx={{
					textDecoration: 'none',
					color: 'inherit',
					cursor: 'pointer',
					'&:hover .banner-arrow': { opacity: 1, transform: 'translateX(0)' },
				}}
			>
				<Overlay />
				<BackgroundImage src={backgroundImageSrc} alt='' aria-hidden='true' />
				<LeftColumn item xs={12} sm={12} md={6} lg={8} display='flex' alignItems='center'>
					<Box>
						<Stack direction='row' spacing={1} alignItems='center' marginBottom={1.5} flexWrap='wrap'>
							<Typography
								component='span'
								sx={{
									fontSize: 12,
									fontWeight: 700,
									letterSpacing: '0.16em',
									textTransform: 'uppercase',
									color: 'rgba(255, 255, 255, 0.92)',
									textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)',
								}}
							>
								<Trans i18nKey='general.dailyPuzzle' />
							</Typography>
							<FiberManualRecordIcon sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: 6 }} fontSize='small' />
							<Typography
								component='span'
								sx={{
									fontSize: 12,
									fontWeight: 500,
									letterSpacing: '0.12em',
									textTransform: 'uppercase',
									color: 'rgba(255, 255, 255, 0.72)',
									textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)',
								}}
							>
								{description}
							</Typography>
						</Stack>
						<BannerTitle variant='h1'>
							{title}
							<Box
								component='span'
								className='banner-arrow'
								aria-hidden='true'
								sx={{
									display: 'inline-block',
									ml: 1.5,
									opacity: 0,
									transform: 'translateX(-8px)',
									transition: 'opacity 0.2s ease, transform 0.2s ease',
								}}
							>
								→
							</Box>
						</BannerTitle>
						{!isMobile && creator && (
							<Box sx={{ mt: 2.5 }}>
								<AuthorInfo
									creator={creator}
									noShowSubtitle
									onClick={(e) => {
										// Don't trigger the banner's puzzle link when the author is clicked.
										e.preventDefault();
										e.stopPropagation();
										window.location.href = `/${creator?.profileLink}`;
									}}
								/>
							</Box>
						)}
					</Box>
				</LeftColumn>
				<RightColumn
					item
					xs={12}
					sm={12}
					md={6}
					lg={4}
					display={{ xs: 'none', sm: 'none', md: 'flex' }}
					alignItems='center'
				>
					<BannerImageContainer>
						<BannerImage src={imageSrc} alt={title} />
					</BannerImageContainer>
				</RightColumn>
			</Container>
		</Wrapper>
	);
};
